import History from '@history';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import authRoles, { RoleEnum, SpecialRolesEnum } from 'app/auth/authRoles';
import { superAdminSelector } from 'app/auth/store/admin.selector';
import { canSwitchFranchiseSelector, userDataSelector, userRoleSelector } from 'app/auth/store/user.selector';
import { logoutUser } from 'app/auth/store/user.thunk';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { webPushSubscriptionSelector } from 'app/main/apps/webPush/ducks/webPush.selector';
import { removeWebPushSubscriptionInfoThunk } from 'app/main/apps/webPush/ducks/webPush.thunk';
import jwtService from 'app/services/jwtService';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

function UserMenu() {
  const dispatch = useDispatch();
  const userRole = useSelector(userRoleSelector);
  const userData = useSelector(userDataSelector);
  const superAdminData = useSelector(superAdminSelector);
  const { t } = useTranslation();
  const { data: subscription } = useSelector(webPushSubscriptionSelector);
  const canSwitchFranchise = useSelector(canSwitchFranchiseSelector);
  const isOnDelegatedFranchise = Boolean(jwtService.getActiveFranchiseId());

  const [userMenu, setUserMenu] = useState<null | HTMLButtonElement>(null);

  const userMenuClick = (event: { currentTarget: HTMLButtonElement }) => {
    setUserMenu(event.currentTarget);
  };

  const userMenuClose = () => {
    setUserMenu(null);
  };

  const userRoleName = useMemo(() => {
    if (!userRole) {
      return null;
    }
    if (authRoles.admin.includes(userRole as RoleEnum)) {
      return t('roles.admin', { ns: 'admin' });
    }
    if (authRoles.registered.includes(userRole as SpecialRolesEnum)) {
      return t('roles.registered', { ns: 'admin' });
    }
    if (userRole === RoleEnum.STORE_OWNER || userRole === SpecialRolesEnum.RESTRICTED_STORE_OWNER) {
      return t('roles.storeOwner', { ns: 'admin' });
    }
    if (userRole === RoleEnum.FRANCHISE_OWNER) {
      return t('roles.franchiseOwner', { ns: 'admin' });
    }
    return null;
  }, [userRole, t]);

  const roleFallbackName = authRoles.admin.includes(userRole as RoleEnum)
    ? t('navigation.displayNameAdmin')
    : t('navigation.displayNameMerchant');
  const displayName = userData.displayName || roleFallbackName;

  return (
    <>
      <Button className="min-h-40" onClick={userMenuClick} aria-label={t('navigation.userMenu', { name: displayName })}>
        <div className="hidden md:flex flex-col mx-4 items-end">
          <Typography component="span" className="normal-case font-bold flex">
            {displayName}
          </Typography>
          {userRoleName && (
            <Typography className="text-11" color="textSecondary">
              {userRoleName}
            </Typography>
          )}
        </div>

        <Avatar className="mx-4">
          <Icon>person</Icon>
        </Avatar>
      </Button>

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
      >
        {!userRoleName || userRoleName.length === 0 ? (
          <>
            <MenuItem component={Link} to="/login" role="button">
              <ListItemIcon className="min-w-40">
                <Icon>lock</Icon>
              </ListItemIcon>
              <ListItemText primary="Login" />
            </MenuItem>
            <MenuItem component={Link} to="/register" role="button">
              <ListItemIcon className="min-w-40">
                <Icon>person_add</Icon>
              </ListItemIcon>
              <ListItemText primary="Register" />
            </MenuItem>
          </>
        ) : (
          <>
            {superAdminData.isSuperAdmin && (
              <>
                <MenuItem
                  onClick={() => {
                    if (subscription) {
                      dispatch(removeWebPushSubscriptionInfoThunk(subscription.id));
                    }
                    userMenuClose();
                    localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
                    localStorage.removeItem(STORAGE_KEYS.START_PAGE_URL);
                    localStorage.removeItem(STORAGE_KEYS.CURRENT_STORE_ID);
                    const impostorId = localStorage.getItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN);
                    localStorage.removeItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN);
                    if (localStorage.getItem(STORAGE_KEYS.USER_ID_LB) !== superAdminData.id) {
                      localStorage.setItem(STORAGE_KEYS.USER_ID_LB, superAdminData.id || '');
                      localStorage.setItem(STORAGE_KEYS.JWT_ACCESS_TOKEN, impostorId || '');
                      window.location.href = window.location.origin + routesUrl.administration;
                    } else {
                      History.push(routesUrl.administration);
                    }
                  }}
                >
                  <ListItemIcon className="min-w-40">
                    <Icon>supervisor_account</Icon>
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.administration')} />
                </MenuItem>
              </>
            )}
            {canSwitchFranchise && (
              <MenuItem
                onClick={() => {
                  userMenuClose();
                  History.push(routesUrl.myBrands);
                }}
              >
                <ListItemIcon className="min-w-40">
                  <Icon>swap_horiz</Icon>
                </ListItemIcon>
                <ListItemText primary={t('navigation.myBrands')} />
              </MenuItem>
            )}
            {isOnDelegatedFranchise && (
              <MenuItem
                onClick={() => {
                  if (subscription) {
                    dispatch(removeWebPushSubscriptionInfoThunk(subscription.id));
                  }
                  userMenuClose();
                  if (jwtService.leaveFranchise()) {
                    window.location.href = window.location.origin;
                  } else {
                    dispatch(logoutUser());
                  }
                }}
              >
                <ListItemIcon className="min-w-40">
                  <Icon>undo</Icon>
                </ListItemIcon>
                <ListItemText primary={t('navigation.leaveFranchise')} />
              </MenuItem>
            )}
            {authRoles.fullAccess.includes(userRole as RoleEnum) && (
              <MenuItem
                onClick={() => {
                  userMenuClose();
                  History.push(routesUrl.owners);
                }}
              >
                <ListItemIcon className="min-w-40">
                  <Icon>account_circle</Icon>
                </ListItemIcon>
                <ListItemText primary={t('navigation.owners')} />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                if (subscription) {
                  dispatch(removeWebPushSubscriptionInfoThunk(subscription.id));
                }
                dispatch(logoutUser());
                userMenuClose();
              }}
            >
              <ListItemIcon className="min-w-40">
                <Icon>exit_to_app</Icon>
              </ListItemIcon>
              <ListItemText primary={t('navigation.logout')} />
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

export default UserMenu;
